<script>
  // @ts-check
  import { createEventDispatcher } from "svelte";

  import Icon from "../../atoms/Icon.svelte";
  import Markdown from "../../atoms/Markdown.svelte";

  /** Primarily used for testing
   * @type {string | undefined} */
  export let id = undefined;

  /** @type {string} name of the input field */
  export let name = undefined;

  /** The label for the input field
   * @type {string} */
  export let label;

  /** The error message to display
   * @type {string | undefined} */
  export let errorMessage = undefined;

  /** Indicates whether the value of the input is valid.
   * @type {boolean} */
  export let isValidValue = false;

  /** The `autoComplete` prop determines whether the browser should automatically complete the input value.
   * @type {string | undefined} */
  export let autoComplete = undefined;

  /** Indicates whether the input field is required or not.
   * @type {boolean} */
  export let required = false;

  /**
   * An optional CSS class to be applied to the component.
   * @type {string}
   */
  export let componentClass = "";

  const transitionClass = "transition-all duration-[400ms] input-easing";

  /** @type {boolean} */
  export let isInvalid = false;

  /** @type {string} */
  export let type = "text";

  /** @type {string} */
  export let pattern = type === "email" ? "[^@\\s]+@[^@\\s]+.[^@\\s]+" : null;

  /** @type {number} */
  export let maxlength = null;

  /** @type {string} */
  export let helpText = "";

  /** @type {string} */
  export let placeholder = "";

  /** @type {boolean} */
  export let autofocus = false;

  export let defaultValue = "";

  /** @type {boolean} This controls whether the label floats */
  export let shouldAnimate = false;

  const dispatcher = createEventDispatcher();

  function handleInputChange(event) {
    const inputLength = event.target.value.length;
    shouldAnimate = true;
    if (inputLength <= 0) {
      shouldAnimate = false;
    }

    if (isInvalid) {
      isInvalid = false;
    }
  }

  $: containerClass = isInvalid ? "border-b-[2px] border-system-red" : "border-b border-black";

  $: labelClass = shouldAnimate ? "opacity-[0]" : "opacity-[10]";

  $: animatedLabelClass = shouldAnimate
    ? "opacity-[10] top-[-20px] md:top-[-22px] xl:top-[-27px]"
    : "opacity-[0] top-[-10px] md:top-[-12px] xl:top-[-15px]";

  $: checkSvgAnimateClass = !isInvalid && isValidValue ? `w-5` : "w-0";
</script>

<div class={componentClass}>
  <div class="relative flex w-full items-center border-b-thin {containerClass}">
    <label for={id} class="heading-xs pointer-events-none absolute left-0 top-0 {labelClass}">
      {label}
      {#if placeholder}
        <span class="sr-only">(format: {placeholder})</span>
      {/if}
    </label>
    <div class="relative mb-3 flex w-full items-center md:mb-4">
      <!-- svelte-ignore a11y-autofocus -->
      <!-- eslint-disable @ObamaFoundation/of-svelte/no-placeholder-values -->
      <input
        value={defaultValue}
        name={name || id}
        autocomplete={autoComplete}
        {type}
        class="heading-xs flex-1 bg-transparent pr-7 outline-none"
        on:input={handleInputChange}
        on:invalid={(event) => {
          event.preventDefault();
          isInvalid = true;
          dispatcher("invalid", { id });
        }}
        aria-invalid={isInvalid}
        aria-describedby={!isInvalid && helpText ? `${id}-help-text` : `${id}-error`}
        on:change
        on:focusin
        on:focusout
        on:keyup
        {id}
        {required}
        {pattern}
        {maxlength}
        {placeholder}
        {autofocus}
      />

      <div class="heading-xs absolute right-0 top-0 h-[1em] w-[1em]">
        {#if isInvalid}
          <Icon inheritSize iconClass="text-system-red " name="alert" />
        {/if}
      </div>
    </div>

    <span
      aria-hidden="true"
      class="label pointer-events-none absolute left-0 {transitionClass} {animatedLabelClass}"
      >{label}</span
    >

    {#if !isInvalid}
      <div
        class="absolute left-[calc(100%-20px)] top-[0.625rem] overflow-hidden xl:top-4.5 {transitionClass} {checkSvgAnimateClass}"
      >
        <Icon name="check" />
      </div>
    {/if}
  </div>

  {#if helpText}
    <Markdown input={helpText} id="{id}-help-text" class="body-sm mt-1 font-gotham" />
  {/if}

  <div id="{id}-error" class="label mt-1 text-system-red" role="alert">
    {#if isInvalid && errorMessage}
      {errorMessage}
    {:else}
      &nbsp <!-- prevent layout shift -->
    {/if}
  </div>
</div>
